import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailOpen, Save, RotateCcw, ExternalLink } from "lucide-react";
import { getEmailText, saveEmailText, emailPreviewUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ORDER = ["bring_items", "bring_tour_item", "bring_footer", "confirm_intro", "ack_intro", "ack_payment", "ack_tour_note", "ack_bike_note", "reminder_intro", "update_intro", "sign_off"];

// Staff-editable wording for customer emails (what to bring, opening lines). {first} {when} {ref} are filled in per booking.
export const EmailTextDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState({});
  useEffect(() => { if (open) getEmailText().then((d) => { setData(d); setText(d.text); }).catch(() => toast.error("Couldn't load email text")); }, [open]);
  const val = (k) => (k === "bring_items" ? (text[k] || []).join("\n") : text[k] ?? "");
  const set = (k, v) => setText((t) => ({ ...t, [k]: k === "bring_items" ? v.split("\n") : v }));
  const save = async () => {
    try {
      await saveEmailText({ ...text, bring_items: (text.bring_items || []).map((s) => s.trim()).filter(Boolean) });
      toast.success("Email wording saved"); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || "Could not save"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0A0A0A] text-[#F5F5F0] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="email-text-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><MailOpen className="w-5 h-5" /> Email wording</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-4">
            <p className="text-xs text-white/45">Use <code>{"{first}"}</code> for the rider's first name, <code>{"{when}"}</code> for the date & time and <code>{"{ref}"}</code> for the booking ref. Leave a box empty to use the default.</p>
            {ORDER.map((k) => (
              <label key={k} className="block">
                <span className="overline text-white/50 flex items-center justify-between gap-2">{data.labels[k]}
                  <button data-testid={`email-text-reset-${k}`} type="button" onClick={() => set(k, k === "bring_items" ? data.defaults[k].join("\n") : data.defaults[k])} className="text-[10px] text-white/40 hover:text-white flex items-center gap-1 normal-case tracking-normal"><RotateCcw className="w-3 h-3" /> default</button>
                </span>
                <Textarea data-testid={`email-text-${k}`} value={val(k)} onChange={(e) => set(k, e.target.value)} rows={k === "bring_items" ? 6 : 2} className="mt-1 bg-white/5 border-white/15 text-white text-sm" />
              </label>
            ))}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button data-testid="save-email-text-btn" onClick={save} className="rounded-full bg-[#F5F5F0] text-[#001A4D] hover:bg-white"><Save className="w-4 h-4 mr-1" /> Save wording</Button>
              <span className="text-xs text-white/40 ml-2">Preview:</span>
              {[["acknowledgement", "Request received"], ["confirmation", "Confirmation"], ["reminder", "Reminder"]].map(([k, l]) => (
                <a key={k} href={emailPreviewUrl(k)} target="_blank" rel="noreferrer" className="h-8 px-3 rounded-full border border-white/15 text-xs flex items-center gap-1 hover:bg-white/10">{l} <ExternalLink className="w-3 h-3" /></a>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
